import { useState } from 'react';
import { useAppSelector } from '@/app/hooks';
import { selectFilters } from './filtersSlice';
import { useLocationOrigin } from './useLocationOrigin';

/** Remembered per browser, so declining once doesn't mean being asked on every search. */
const DISMISSED_KEY = 'mmef.locationPrompt.dismissed';

function alreadyDismissed(): boolean {
  try {
    return window.localStorage.getItem(DISMISSED_KEY) === '1';
  } catch {
    // Private windows and blocked site data throw on access rather than returning null. Not being
    // able to remember a dismissal is a reason to show the prompt, not to break the page.
    return false;
  }
}

/**
 * An offer to measure distances from where the reader actually is.
 *
 * <p><b>Why it exists.</b> Picking "London" measures every distance from one fixed point near
 * Trafalgar Square, so someone in Harrow is told an event is 1.6 miles away when it is nowhere
 * near them. The number isn't wrong, but the column reads as "from you", and only the reader can
 * supply the location that would make that true.
 *
 * <p><b>Why it isn't a dialog.</b> The browser's own permission prompt is the interruption; a
 * second one in front of it would be two. This is a line above the results that can be ignored,
 * and asks the browser only once someone has said yes to being asked.
 *
 * <p>Hidden entirely once distances already come from the reader — an offer to do the thing you
 * are already doing is just noise.
 */
export function LocationPrompt() {
  const { origin } = useAppSelector(selectFilters);
  const { request, locating, error } = useLocationOrigin();
  const [dismissed, setDismissed] = useState(alreadyDismissed);

  const dismiss = () => {
    setDismissed(true);
    try {
      window.localStorage.setItem(DISMISSED_KEY, '1');
    } catch {
      // Dismissed for this visit even if it can't be remembered for the next one.
    }
  };

  // Nothing to offer without a search to improve, and nothing to fix once it's already theirs.
  if (!origin || origin.source === 'geo') return null;
  // An error is worth showing even after a dismissal — it explains a button that just failed.
  if (dismissed && !error) return null;
  // Truthiness, not `in`: the property can be present and undefined, and an offer that leads to a
  // button which cannot work is worse than no offer.
  if (!navigator.geolocation) return null;

  return (
    <div className="location-prompt">
      <p className="location-prompt__text">
        Distances are measured from the centre of <strong>{origin.label}</strong>, not from you.{' '}
        {error ?? 'Use your location for distances from where you actually are?'}
      </p>
      <div className="location-prompt__actions">
        <button type="button" className="button" onClick={request} disabled={locating}>
          {locating ? 'Finding you…' : error ? 'Try again' : 'Use my location'}
        </button>
        <button type="button" className="button button--quiet" onClick={dismiss}>
          No thanks
        </button>
      </div>
    </div>
  );
}
